import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Building2, X } from 'lucide-react'
import { useUpdateOrganization } from '../hooks'
import { useToast } from '@/hooks/use-toast'
import { useIndustries } from '@/features/industries/hooks'
import type { Organization } from '../types'

interface EditOrganizationFormProps {
    organization: Organization
    onClose: () => void
    onSuccess?: () => void
}

export function EditOrganizationForm({ organization, onClose, onSuccess }: EditOrganizationFormProps) {
    const [formData, setFormData] = useState({
        name: organization.name || '',
        description: organization.description || '',
        industry_id: organization.industry_id || '',
        size: organization.size || '',
    })
    const [isSubmitting, setIsSubmitting] = useState(false)
    const { toast } = useToast()
    const updateMutation = useUpdateOrganization()

    const { data: industries, isLoading: industriesLoading } = useIndustries()

    const sizeOptions = [
        'Small (1-50 employees)',
        'Medium (51-500 employees)',
        'Large (501-5000 employees)',
        'Enterprise (5000+ employees)',
    ]

    const handleSubmit = async (e: globalThis.React.FormEvent) => {
        e.preventDefault()
        if (!formData.name.trim()) {
            toast({ title: 'Error', description: 'Organization name is required.', variant: 'destructive' })
            return
        }
        setIsSubmitting(true)
        try {
            await updateMutation.mutateAsync({ id: organization.id, data: formData })
            toast({
                title: 'Organization Updated',
                description: `"${formData.name}" has been updated.`,
            })
            onSuccess?.()
            onClose()
        } catch (error: any) {
            toast({
                title: 'Error',
                description: error.response?.data?.title || 'Failed to update organization.',
                variant: 'destructive',
            })
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleChange = (field: string, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }))
    }

    return (
        <div className="fixed inset-0 !mt-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-md">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                    <div>
                        <CardTitle className="flex items-center space-x-2">
                            <Building2 className="h-5 w-5" />
                            <span>Edit Organization</span>
                        </CardTitle>
                        <CardDescription>
                            Update details for {organization.name}
                        </CardDescription>
                    </div>
                    <Button variant="ghost" size="icon" onClick={onClose}>
                        <X className="h-4 w-4" />
                    </Button>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <Label htmlFor="edit-name">Organization Name *</Label>
                            <input
                                id="edit-name"
                                type="text"
                                value={formData.name}
                                onChange={(e) => handleChange('name', e.target.value)}
                                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="Enter organization name"
                                required
                            />
                        </div>

                        <div>
                            <Label htmlFor="edit-description">Description</Label>
                            <textarea
                                id="edit-description"
                                value={formData.description}
                                onChange={(e) => handleChange('description', e.target.value)}
                                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                rows={3}
                                placeholder="Brief description of your organization"
                            />
                        </div>

                        <div>
                            <Label htmlFor="edit-industry">Industry</Label>
                            <select
                                id="edit-industry"
                                value={formData.industry_id}
                                onChange={(e) => setFormData(prev => ({ ...prev, industry_id: e.target.value }))}
                                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                disabled={industriesLoading}
                            >
                                <option value="">Select industry</option>
                                {industries?.map(industry => (
                                    <option key={industry.id} value={industry.id}>
                                        {industry.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <Label htmlFor="edit-size">Organization Size</Label>
                            <select
                                id="edit-size"
                                value={formData.size}
                                onChange={(e) => setFormData(prev => ({ ...prev, size: e.target.value }))}
                                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="">Select size</option>
                                {sizeOptions.map(size => (
                                    <option key={size} value={size}>
                                        {size}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="flex justify-end space-x-3 pt-4">
                            <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
                                Cancel
                            </Button>
                            <Button type="submit" disabled={isSubmitting}>
                                {isSubmitting ? 'Saving...' : 'Save Changes'}
                            </Button>
                        </div>
                    </form>
                </CardContent>
            </Card>
        </div>
    )
}
